<script>
export default {
	name: 'FlightHotelSearch',
	inheritAttrs: false,
};
</script>

<script setup>
import MobileSubpage from '~/components/BookingWidget/MobileSubpage.vue';
import useBookingWidgetSubpage from '~/logic/composables/useBookingWidgetSubpage.js';
import VueTabs from '~/components/VueTabs.vue';
import SubTabFlightAndHotel from '~/components/BookingWidget/FlightHotelSearch/SubTabFlightAndHotel.vue';
import SubTabHotel from '~/components/BookingWidget/FlightHotelSearch/SubTabHotel.vue';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';
import { pushAA_flightPlusHotelStart, pushAA_flightPlusHotelEnd, pushAA_click } from '~/logic/adobe-analytic.js';
import ExperienceFragment from '../ExperienceFragment.vue';
import MobileSubPageSwiper from '~/components/BookingWidget/MobileSubPageSwiper.vue';
import { getCookie } from '~/logic/helpers/cookies.js';

const props = defineProps({
	// modelValue: { type: String, default: '' },
	flightHotelSearch: { type: Object, default: null },
	hotelTab: { type: Object, default: null },
	mhHolidays: { type: Object, default: null },
	flightHotelSearchApiURL: { type: String, default: '' },
	hotelSearchApiURL: { type: String, default: '' },
	isInBookingWidget: { type: Boolean, default: false },
	dropDownHotelListApiUrl: { type: String, default: '' },
	cultureCode: { type: String, default: '' },
	mobileSwiperTabs: { type: Array, default: () => [] },
});

const emit = defineEmits([
	'update:active-sub-tab',
]);

const rootEl = ref(null);

const { isMobileSubpageVisible } = useBookingWidgetSubpage({
	hashKey: ['flight-hotel-search', 'flight-hotel-search-tab'],
	rootEl,
});

const activeSubTab = ref('flight-hotel');
const { isEditorMode } = useGlobalAEMState();

//  AA for sub tab click tracking
watch(activeSubTab, (newTabName) => {
	pushAA_click([
		{
			clickName: newTabName,
			clickComponentType: 'Button',
			componentName: 'FlightHotelSearch',
			componentID: props.componentIdAA,
		},
		{
			name: newTabName,
			type: 'other',
		},
	]);
});


let AAHasSentStart = false;
let AAHasSentEnd = false;
const blendedHeaderCss = ref('');
const mobileProperties = ref(null);

onMounted(() => {
	const aaFormStartHandler = (event) => {
		if (AAHasSentStart) return;
		if (event.target.tagName === 'FORM') return;

		AAHasSentStart = true;
		pushAA_flightPlusHotelStart(activeSubTab.value);
		rootEl.value.removeEventListener('focus', aaFormStartHandler, { capture: true });
	};
	rootEl.value.addEventListener('focus', aaFormStartHandler, { capture: true });

	const header = document.querySelector('.global-header');
	if (!header) return;
	if (header.classList.contains('fixed')) {
		blendedHeaderCss.value = 'blended-header-css';
	} else {
		blendedHeaderCss.value = '';
	}
	mobileProperties.value = getCookie('mobileProperties');
});

const sendFormCompleteAA = (subtabName) => {
	if (AAHasSentEnd) return;
	AAHasSentEnd = true;
	pushAA_flightPlusHotelEnd(subtabName);
};

</script>



<template>
<div
	v-if="!props.isHideInDesktop"
	ref="rootEl"
	class="FlightHotelSearch booking-widget-subcomponent"
	:class="{
		'isEditorMode': isEditorMode,
	}"
>
	<div class="generic-container">
		<h5 v-if="isEditorMode" class="title-editor-mode">
			<icon-fas-plane class="mr-2 fill-current inline-block" />
			<span>Booking Widget: Flight Hotel + Search</span>
		</h5>

		<VueTabs
			v-model="activeSubTab"
			:dormantMode="isEditorMode"
			renderPanelsWithVShow
			:enableGlassDesign="props.flightHotelSearch?.enableGlassDesign || props.hotelTab?.enableGlassDesign"
			@update:modelValue="$emit('update:active-sub-tab', $event)"
		>
			<template v-if="!props.flightHotelSearch?.isHideInDesktop && props.flightHotelSearch?.label" #tab-flight-hotel>
				<div class="flex items-center justify-center p-4">
					<span class="inline-block ml-1">{{ props.flightHotelSearch?.label }}</span>
				</div>
			</template>
			<template v-if="!props.hotelTab?.isHideInDesktop && props.hotelTab?.label" #tab-hotel>
				<div class="flex items-center justify-center p-4">
					<span class="inline-block ml-1">{{ props.hotelTab?.label }}</span>
				</div>
			</template>
			<template v-if="!props.mhHolidays?.isHideInDesktop && props.mhHolidays?.subHeaderTab3Label" #link-mhholidays>
				<AppHyperlink
					v-aa="[
						{
							clickName: props.mhHolidays?.subHeaderTab3Label,
							clickComponentType: 'Button',
							componentName: 'FlightHotelSearch',
							componentID: props.componentIdAA,
						},
						{
							name: props.mhHolidays?.subHeaderTab3Label,
							type: isUrlMatchCurrentHostname(props.mhHolidays?.ctaPath) ? 'other' : 'exit',
						},
					]"
					:href="props.mhHolidays?.ctaPath"
					class="font-semibold text-$text-color rounded-xl px-4 hover:bg-neutral-grey-extralight h-full leading-none"
					:target="props.mhHolidays.openInNewTab ? '_blank' : '_self'"
					:autoExternalLinkIcon="true"
				>{{ props.mhHolidays?.subHeaderTab3Label }}</AppHyperlink>
			</template>

			<div
				v-if="!props.flightHotelSearch?.isHideInDesktop && props.flightHotelSearch?.label"
				identifier="flight-hotel"
				:class="{
					'py-10': isEditorMode,
				}"
			>
				<ExperienceFragment
					v-if="props.flightHotelSearch.widgetType === 'embedWidget'"
					class="flex-grow mt-4"
					:xfPath="props.flightHotelSearch.embedWidget"
					:showSpinner="true"
				/>
				<SubTabFlightAndHotel
					v-else
					class="pt-6"
					:datePicker="props.flightHotelSearch.datePicker"
					:description="props.flightHotelSearch.description"
					:tooltipLabel="props.flightHotelSearch.tooltipLabel"
					:tooltipBody="props.flightHotelSearch.tooltipBody"
					:ondSelection="props.flightHotelSearch.ondSelection"
					:travellerSelection="props.flightHotelSearch.travellerSelection"
					:cabinClass="props.flightHotelSearch.cabinClass"
					:note="props.flightHotelSearch.note"
					:productID="props.flightHotelSearch.productID"
					:endpoint="props.flightHotelSearchApiURL"
					:cultureCode="props.cultureCode"
					:enableGlassDesign="props.flightHotelSearch?.enableGlassDesign"
					@form-submission="sendFormCompleteAA('flight-hotel', $event)"
				></SubTabFlightAndHotel>
			</div>
			<div
				v-if="!props.hotelTab?.isHideInDesktop && props.hotelTab?.label"
				identifier="hotel"
				:class="{
					'py-10': isEditorMode,
				}"
			>
			
				<ExperienceFragment
					v-if="props.hotelTab.widgetType === 'embedWidget'"
					class="flex-grow"
					:xfPath="props.hotelTab.embedWidget"
					:showSpinner="true"
				/>
				<SubTabHotel
					v-else
					class="pt-6"
					:datePicker="props.hotelTab.datePicker"
					:description="props.hotelTab.description"
					:tooltipLabel="props.hotelTab.tooltipLabel"
					:tooltipBody="props.hotelTab.tooltipBody"
					:hotelSelection="props.hotelTab.hotelSelection"
					:travellerSelection="props.hotelTab.travellerSelection"
					:note="props.hotelTab.note"
					:productID="props.hotelTab.productID"
					:endpoint="props.hotelSearchApiURL"
					:hotelSearchPropertyUrl="props.hotelTab.hotelSearchPropertyUrl"
					:dropDownHotelListApiUrl="props.dropDownHotelListApiUrl"
					:cultureCode="props.cultureCode"
					:enableGlassDesign="props.hotelTab?.enableGlassDesign"
					@form-submission="sendFormCompleteAA('hotel', $event)"
				></SubTabHotel>
			</div>
		</VueTabs>
	</div>
</div>
<MobileSubpage
	:isVisible="isMobileSubpageVisible"
	:mobileSubPageDesign="props.flightHotelSearch?.enableGlassDesign || props.hotelTab?.enableGlassDesign ? 'glass-design' : 'MH'"
	:enableGlassDesign="props.flightHotelSearch?.enableGlassDesign || props.hotelTab?.enableGlassDesign"
	:blendedHeaderCss="blendedHeaderCss"
	class="text-white"
	:mobileSwiperTabs="props.mobileSwiperTabs"
	style="--overlay-container-bg-color: var(--secondary-blue-extradark);"
	@update:is-visible="isMobileSubpageVisible = $event"
>

	<template #banner-title>
		<div>
			<h1 v-if="!props.flightHotelSearch?.enableGlassDesign" class="text-center">{{ $t('Flight & Hotel Search') }}</h1>
			<h3 v-else class="text-center">{{ $t('Flight & Hotel Search') }}</h3>
		</div>
	</template>

	<template #swiper>
		<slot name="banner-title">
			<MobileSubPageSwiper
				:mobileSwiperTabs="props.mobileSwiperTabs.length === 0 ? JSON.parse(mobileProperties) : props.mobileSwiperTabs"
				activeTab="#flight-hotel-search"
			/>
		</slot>
	</template>

	<template #default>
		<div class="generic-container" :class="{ 'mt-4': props.flightHotelSearch?.enableGlassDesign }">
			<VueTabs
				v-model="activeSubTab"
				:dormantMode="isEditorMode"
				:enableGlassDesign="props.flightHotelSearch?.enableGlassDesign || props.hotelTab?.enableGlassDesign"
				renderPanelsWithVShow
				@update:modelValue="$emit('update:active-sub-tab', $event)"
			>
				<template v-if="!props.flightHotelSearch?.isHideInMobile" #tab-flight-hotel>
					<div class="flex items-center justify-center p-4">
						<span class="inline-block ml-1">{{ props.flightHotelSearch?.label }}</span>
					</div>
				</template>
				<template v-if="!props.hotelTab?.isHideInMobile" #tab-hotel>
					<div class="flex items-center justify-center p-4">
						<span class="inline-block ml-1">{{ props.hotelTab?.label }}</span>
					</div>
				</template>
				<template v-if="!props.mhHolidays?.isHideInMobile && props.mhHolidays?.subHeaderTab3Label" #link-mhholidays>
					<AppHyperlink
						v-aa="[
							{
								clickName: props.mhHolidays?.subHeaderTab3Label,
								clickComponentType: 'Button',
								componentName: 'FlightHotelSearch',
								componentID: props.componentIdAA,
							},
							{
								name: props.mhHolidays?.subHeaderTab3Label,
								type: isUrlMatchCurrentHostname(props.mhHolidays?.ctaPath) ? 'other' : 'exit',
							},
						]"
						:href="props.mhHolidays?.ctaPath"
						class="font-semibold text-$text-color rounded-xl px-4 hover:bg-neutral-grey-extralight h-full leading-none"
						:target="props.mhHolidays.openInNewTab ? '_blank' : '_self'"
						:autoExternalLinkIcon="true"
					>{{ props.mhHolidays?.subHeaderTab3Label }}</AppHyperlink>
				</template>

				<div v-if="!props.flightHotelSearch?.isHideInMobile" identifier="flight-hotel">
					<ExperienceFragment
						v-if="props.flightHotelSearch.widgetType === 'embedWidget'"
						class="flex-grow mt-4"
						:xfPath="props.flightHotelSearch.embedWidget"
						:showSpinner="true"
					/>
					<SubTabFlightAndHotel
						v-else
						class="pt-6"
						:datePicker="props.flightHotelSearch.datePicker"
						:description="props.flightHotelSearch.description"
						:tooltipLabel="props.flightHotelSearch.tooltipLabel"
						:tooltipBody="props.flightHotelSearch.tooltipBody"
						:ondSelection="props.flightHotelSearch.ondSelection"
						:travellerSelection="props.flightHotelSearch.travellerSelection"
						:cabinClass="props.flightHotelSearch.cabinClass"
						:note="props.flightHotelSearch.note"
						:productID="props.flightHotelSearch.productID"
						:endpoint="props.flightHotelSearchApiURL"
						:cultureCode="props.cultureCode"
						:enableGlassDesign="props.flightHotelSearch?.enableGlassDesign"
						@form-submission="sendFormCompleteAA('flight-hotel', $event)"
					></SubTabFlightAndHotel>
				</div>

				<div v-if="!props.hotelTab?.isHideInMobile" identifier="hotel">
					<ExperienceFragment
						v-if="props.hotelTab.widgetType === 'embedWidget'"
						class="flex-grow"
						:xfPath="props.hotelTab.embedWidget"
						:showSpinner="true"
					/>
					<SubTabHotel
						v-else
						class="pt-6"
						:datePicker="props.hotelTab.datePicker"
						:description="props.hotelTab.description"
						:tooltipLabel="props.hotelTab.tooltipLabel"
						:tooltipBody="props.hotelTab.tooltipBody"
						:hotelSelection="props.hotelTab.hotelSelection"
						:travellerSelection="props.hotelTab.travellerSelection"
						:note="props.hotelTab.note"
						:productID="props.hotelTab.productID"
						:endpoint="props.hotelSearchApiURL"
						:hotelSearchPropertyUrl="props.hotelTab.hotelSearchPropertyUrl"
						:dropDownHotelListApiUrl="props.dropDownHotelListApiUrl"
						:cultureCode="props.cultureCode"
						:enableGlassDesign="props.hotelTab?.enableGlassDesign"
						@form-submission="sendFormCompleteAA('hotel', $event)"
					></SubTabHotel>
				</div>
			</VueTabs>
		</div>
	</template>

</MobileSubpage>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.FlightHotelSearch {

}

</style>
